/**
 * RankedContractsTable: section 3 of the Screener page -- the Order toggle, how-to-read note, queue action bar,
 * banners and the ranked table. Moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28).
 * The ticket path (queueBlocker, resolveTicketSizing, stageSelectedPurchases, stageSinglePurchase) stays in
 * ScreenView and is passed in; this component calls it, it does not reimplement it.
 */
import React from "react";
import { Card, CardContent } from "@components/ui/card";
import { badgeStyle, cardStyle, mutedText, secondaryText, toneStyles, ui } from "@components/screener/screenerUi";
import { priorityExplainer, priorityToggleLabel } from "@lib/screenerTruth";
import { type Purchase, type ScreenerResponse, type SortDir, type SortKey, type TicketState, type Tone } from "@lib/screenerTypes";
import { HelionButton } from "@components/ui/HelionButton";
import ScreenerBudgetLine from "@components/screener/ScreenerBudgetLine";
import { describeLotRule, type LotMinimumRule, type LotRefusal, type ScreenerLotPreview } from "@lib/lotMinimum";
import { isRankedCandidate, isSelectedCandidate, titleizeSkipReason } from "@lib/screenerViewHelpers";
import RankedContractRow from "@components/screener/RankedContractRow";
import RankedContractDetail from "@components/screener/RankedContractDetail";
import { type ScreenerBudget } from "@lib/screenerBudget";

// Ranked-table column counts. MUST equal the <th>/<td> actually rendered, or the expanded drawer row misaligns.
// Base = checkbox + Expiry, Strike, Mid, Spread %, Qty, Desk, Priority·rung, Vega/$1k, Grade, Entry Score, Surface
// Score, Value, Shape, Tenor, DTE, OI (Phase 2, 2026-10-28: identity + qty first; Notional and Vega USD/pt cut).
// Analytics = 12 columns (the duplicate analytics Spread % cut) + the "Insight" header.
export const SCREENER_BASE_COLUMNS = 17;
export const SCREENER_ANALYTICS_COLUMNS = 13;

export type RankedContractsTableProps = {
  allCandidateRows: Purchase[];
  budgetTally: ScreenerBudget;
  candidateUniverseSummary: { total: number; selected: number; ranked: number; skipped: number; };
  cioQtyByConid: Record<string, string>;
  data: ScreenerResponse;
  expandedContractKey: string | null;
  lotExceptionByConid: Record<string, string>;
  lotPreviewFor: (p: Purchase) => ScreenerLotPreview;
  lotRule: LotMinimumRule | null;
  marketRankOnly: boolean;
  pendingReadFailed: string | null;
  priorityRankAvailable: boolean;
  queueBlocker: (p: Purchase) => string | null;
  resolveTicketSizing: (p: Purchase) => { qty: number; notional: number | null; origin: "SCREENER" | "CIO"; cioSized: boolean; };
  rungByExpiry: Map<string, { rollingOff?: boolean | null | undefined; status?: string | null | undefined; }>;
  selectedForQueue: Set<string>;
  selectedQueueCount: number;
  setCioQtyByConid: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setExpandedContractKey: React.Dispatch<React.SetStateAction<string | null>>;
  setLotExceptionByConid: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setLotRefusedByConid: React.Dispatch<React.SetStateAction<Record<string, LotRefusal>>>;
  setSelectedForQueue: React.Dispatch<React.SetStateAction<Set<string>>>;
  setShowAnalyticsColumns: React.Dispatch<React.SetStateAction<boolean>>;
  setSortDir: React.Dispatch<React.SetStateAction<SortDir>>;
  setSortKey: React.Dispatch<React.SetStateAction<SortKey>>;
  showAnalyticsColumns: boolean;
  sortedPurchases: Purchase[];
  sortDir: SortDir;
  sortKey: SortKey;
  stageBanner: { tone: Tone; text: string; } | null;
  stagedTicketSummary: { total: number; queued: number; newlyStaged: number; };
  stageSelectedPurchases: () => Promise<void>;
  stageSinglePurchase: (p: Purchase) => Promise<void>;
  stagingAll: boolean;
  stagingConid: string | null;
  toggleSort: (key: SortKey) => void;
  ticketStateByConid: Record<string, TicketState>;
  trustHardBlock: boolean;
  trustHardBlocker: string | null;
};

export default function RankedContractsTable({
  allCandidateRows,
  budgetTally,
  candidateUniverseSummary,
  cioQtyByConid,
  data,
  expandedContractKey,
  lotExceptionByConid,
  lotPreviewFor,
  lotRule,
  marketRankOnly,
  pendingReadFailed,
  priorityRankAvailable,
  queueBlocker,
  resolveTicketSizing,
  rungByExpiry,
  selectedForQueue,
  selectedQueueCount,
  setCioQtyByConid,
  setExpandedContractKey,
  setLotExceptionByConid,
  setLotRefusedByConid,
  setSelectedForQueue,
  setShowAnalyticsColumns,
  setSortDir,
  setSortKey,
  showAnalyticsColumns,
  sortedPurchases,
  sortDir,
  sortKey,
  stageBanner,
  stagedTicketSummary,
  stageSelectedPurchases,
  stageSinglePurchase,
  stagingAll,
  stagingConid,
  toggleSort,
  ticketStateByConid,
  trustHardBlock,
  trustHardBlocker,
}: RankedContractsTableProps) {
  const Arrow = ({ active, dir }: { active: boolean; dir: SortDir }) => (
    <span
      className="ml-1 inline-block select-none"
      aria-hidden
      style={{ color: ui.textMuted }}
    >
      {active ? (dir === "asc" ? "▲" : "▼") : "▵"}
    </span>
  );

  const thClass = "px-3 py-3 text-left font-semibold";
  const thStyle: React.CSSProperties = { color: ui.textSecondary, whiteSpace: "nowrap" };
  const sortableTh = (key: SortKey, label: string) => (
    <th key={key} className={`cursor-pointer select-none ${thClass}`} style={thStyle} onClick={() => toggleSort(key)} role="button" tabIndex={0}>
      {label}{" "}
      <Arrow active={sortKey === key} dir={sortDir} />
    </th>
  );
  // Non-interactive (audit table item 13): these columns have no sort key, or sort on a constant, so a click did nothing.
  const staticTh = (key: string, label: string, title?: string) => (
    <th key={key} className={thClass} style={thStyle} title={title} data-sortable="false">
      {label}
    </th>
  );

  return (
    <section data-screener-section="ranked" data-testid="screener-ranked-section" className="mb-4">
    <Card style={cardStyle}>
      <CardContent className="p-0">
        <div className="flex flex-col gap-3 border-b px-4 py-4 md:flex-row md:items-center md:justify-between" style={{ borderColor: ui.cardBorder }}>
          <div>
            <div className="text-lg font-semibold">
              Ranked contracts{" "}
              <span className="text-sm font-normal" style={mutedText}>
                ({sortKey === "priorityRank" ? "Priority order" : sortKey === "entryScore" ? "Entry Score order" : "sorted by a column"} · {candidateUniverseSummary.ranked || candidateUniverseSummary.total} ranked)
              </span>
            </div>
            <div className="mt-1 text-xs" style={mutedText}>
              Every contract that passed the filters. Tick a row, give it a quantity, queue it to the Trading Desk.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1 rounded-lg border px-1 py-0.5 text-xs"
              style={{ borderColor: ui.cardBorder }}
              data-testid="screener-order-toggle"
              title={priorityExplainer(data?.ladderPriority)}
            >
              <span className="px-1" style={mutedText}>Order</span>
              {(
                [
                  ["priorityRank", priorityToggleLabel(data?.ladderPriority)],
                  ["entryScore", "Entry Score"],
                ] as [SortKey, string][]
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    setSortKey(k);
                    setSortDir(k === "entryScore" ? "desc" : "asc");
                  }}
                  className="rounded-md px-2 py-1"
                  style={
                    sortKey === k
                      ? { backgroundColor: "rgba(148, 163, 184, 0.18)", color: ui.textPrimary, fontWeight: 600 }
                      : { color: ui.textSecondary }
                  }
                >
                  {label}
                </button>
              ))}
            </span>
            {sortKey === "priorityRank" && allCandidateRows.length > 0 && !priorityRankAvailable ? (
              <span style={badgeStyle("warning")} data-testid="screener-priority-unavailable">
                Priority order not published by this backend: showing Entry Score order
              </span>
            ) : null}
            <HelionButton
              onClick={() => setShowAnalyticsColumns((prev) => !prev)}
              variant="secondary"
              type="button"
            >
              {showAnalyticsColumns ? "Show PM View" : "Show Full Analytics"}
            </HelionButton>
          </div>
        </div>

        <details className="border-b px-4 py-2" style={{ borderColor: ui.cardBorder }} data-testid="screener-how-to-read">
          <summary className="cursor-pointer text-xs font-semibold" style={secondaryText}>
            How to read Priority, Grade and Entry Score
          </summary>
          <div className="mt-2 max-w-[1600px] text-[11px] leading-5" style={{ color: ui.textSecondary }}>
            Entry Score is a vega/surface blend multiplied by an unbounded tenor tilt (1 + DTE/1000), but the A–F cut-points (0.85/0.70/0.55/0.40) are absolute, so <span className="font-semibold">longer tenor reads as higher quality</span>. Measured on a live 40-candidate universe (2026-08-12): all five A grades were tenor-driven and none was an A on the pre-tilt blend; past roughly 1125 DTE an F-grade blend earns an A. Scores above 1.0 are clamped for grading only, so <span className="font-semibold">1.840 and 0.851 both show a grade of A</span> while the unclamped score is displayed beside it. Surface value labels rank against a mixed-frequency history (monthly to 2026-03, then daily), which concentrates about 36% of the percentile weight in the most recent 2.7% of the record and reads the surface <span className="font-semibold">cheaper</span> than a calendar-balanced basis would. <span data-testid="screener-priority-explainer">{priorityExplainer(data?.ladderPriority)}</span> Switch to Entry Score with the Order toggle. <span className="font-semibold">Grade is itself a sortable column</span> — sorting by it reorders the list you buy from. Size on the underlying drivers, not the letter. The entry ML model carries zero weight in the rank and is shown as context only.
          </div>
        </details>

        {/* Queue action bar — beside the checkboxes. Selecting contracts and queueing them is one motion. */}
        <div
          className="flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:items-center md:justify-between"
          style={{ borderColor: ui.cardBorder, backgroundColor: selectedQueueCount ? "rgba(148, 163, 184, 0.07)" : "transparent" }}
        >
          <div className="text-xs" style={mutedText}>
            {selectedQueueCount > 0 ? (
              <>
                <span className="font-semibold" style={{ color: ui.textPrimary }}>
                  {selectedQueueCount} contract{selectedQueueCount === 1 ? "" : "s"} ready to queue
                </span>
                {selectedForQueue.size > selectedQueueCount ? (
                  <span style={{ color: "rgb(var(--c-warn))" }}>
                    {" "}· {selectedForQueue.size - selectedQueueCount} ticked but not sized
                  </span>
                ) : null}
              </>
            ) : selectedForQueue.size > 0 ? (
              <span style={{ color: "rgb(var(--c-warn))" }}>
                {selectedForQueue.size} ticked — enter a quantity on each to queue
              </span>
            ) : (
              <>Tick contracts below to queue them to the Trading Desk, then give each a quantity.</>
            )}

            {/* RUNNING DEPLOYMENT BUDGET (CIO, 2026-09-17). Estimated at the mid, so it
                is labelled that way: the fill happens by hand in TWS. Over budget is
                stated in amber and changes nothing - Queue Selected stays enabled.
                Lives in its own component so it can be render-tested; the uirender
                harness covers this page's context panels only. */}
            <ScreenerBudgetLine tally={budgetTally} textPrimary={ui.textPrimary} />

            {/* THE CONTRACT MINIMUM, STATED (CIO, 2026-09-17). Standing text, shown
                whether or not anything is ticked. The rule used to speak only at the
                moment of refusal, which made a live control look like an absent one.
                "unknown" and "off" are said out loud; silence would read as "on". */}
            {(() => {
              const line = describeLotRule(lotRule);
              return (
                <div
                  className="mt-2"
                  data-tone={line.state === "on" ? "neutral" : "warn"}
                  style={line.state === "on" ? undefined : { color: "rgb(var(--c-warn))" }}
                >
                  {line.text}
                </div>
              );
            })()}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs" style={mutedText} data-testid="screener-queue-counts">
              Already queued {stagedTicketSummary.queued} · queued this session {stagedTicketSummary.newlyStaged}
            </span>
            {selectedForQueue.size > 0 ? (
              <HelionButton
                type="button"
                onClick={() => {
                  setSelectedForQueue(new Set());
                  setCioQtyByConid({});
                  setLotExceptionByConid({});
                  setLotRefusedByConid({});
                }}
                disabled={stagingAll}
              >
                Clear selection
              </HelionButton>
            ) : null}
            <HelionButton
              type="button"
              onClick={stageSelectedPurchases as any}
              disabled={stagingAll || trustHardBlock || selectedQueueCount === 0}
              title={
                trustHardBlock
                  ? "Staging is locked by a data or invariant guardrail"
                  : selectedQueueCount === 0
                  ? "Tick contracts and give any unsized ones a quantity"
                  : `Queue the ${selectedQueueCount} contract${selectedQueueCount === 1 ? "" : "s"} you have ticked`
              }
              className="font-semibold"
              style={selectedQueueCount ? { borderColor: "rgb(var(--c-accent))" } : undefined}
            >
              {stagingAll
                ? "Queueing…"
                : selectedQueueCount === 0
                ? "Queue Selected"
                : `Queue Selected (${selectedQueueCount})`}
            </HelionButton>
            <HelionButton
              onClick={() => { window.location.href = "/trading-desk"; }}
              className="bg-transparent"
              type="button"
            >
              Open Trading Desk
            </HelionButton>
          </div>
        </div>
        {trustHardBlock || stageBanner || pendingReadFailed ? (
          <div className="space-y-2 border-b px-4 py-3" style={{ borderColor: ui.cardBorder }}>
            {trustHardBlock && (
              <div className="max-w-3xl text-sm" style={{ ...toneStyles.danger, borderRadius: 12, padding: "10px 12px" }}>
                Screener staging is locked by a hard data or invariant guardrail. {trustHardBlocker || "Review Data Confidence and Guardrail Status."}
              </div>
            )}
            {stageBanner && (
              <div className="text-sm" style={{ ...toneStyles[stageBanner.tone], borderRadius: 12, padding: "12px 14px" }}>
                {stageBanner.text}
              </div>
            )}
            {pendingReadFailed ? (
              <div className="text-xs" style={{ color: "rgb(var(--c-warn))" }} data-testid="screener-pending-read-failed">
                {pendingReadFailed}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="w-full overflow-x-auto" data-testid="screener-ranked-scroller">
        <table className="min-w-full text-sm" style={{ width: "100%" }}>
          <thead style={{ backgroundColor: ui.rowHeader }}>
            <tr>
              {/* Select-all removed 2026-10-28 (Phase 2): it ticked allocator BUYs only, so it was always disabled. */}
              <th className={thClass} style={thStyle} data-col="select">
                <span className="sr-only">Select</span>
              </th>
              {/* Identity and quantity FIRST (Phase 2): at 1500 px the old order put Strike, Mid and the Qty box
                  off-screen. Keep SCREENER_BASE_COLUMNS / SCREENER_ANALYTICS_COLUMNS in step with this list. */}
              {sortableTh("expiry", "Expiry")}
              {sortableTh("strike", "Strike")}
              {sortableTh("mid", "Mid")}
              {sortableTh("roundTripSpreadPct", "Spread %")}
              {sortableTh("allocatedQty", "Qty")}
              <th className={thClass} style={thStyle}>
                Desk
              </th>
              {staticTh("skipReason", "Priority · rung", "Priority # (ladder first), Entry Score #, and the rung chip. Use the Order toggle to re-sort.")}
              {sortableTh("vegaPer1k", "Vega/$1k")}
              {sortableTh("entryGrade", "Grade")}
              {sortableTh("entryScore", "Entry Score")}
              {sortableTh("surfaceScore", "Surface Score")}
              {sortableTh("surfaceValueLabel", "Value")}
              {sortableTh("surfaceShapeLabel", "Shape")}
              {sortableTh("tenorBucket", "Tenor")}
              {sortableTh("dte", "DTE")}
              {sortableTh("openInterest", "OI")}
              {/* Notional and Vega USD/pt cut 2026-10-28 (Phase 2): allocator-only, always "—" in market-rank mode. */}
              {showAnalyticsColumns ? (
                <>
                  {sortableTh("iv", "IV")}
                  {sortableTh("moneyness", "Moneyness")}
                  {sortableTh("conid", "Conid")}
                  {sortableTh("bid", "Bid")}
                  {sortableTh("ask", "Ask")}
                  {sortableTh("unitCost", "Unit Cost")}
                  {sortableTh("vega", "Vega")}
                  {staticTh("liquidityTier", "Liquidity")}
                  {/* The analytics "Spread %" was cut 2026-10-28 (audit table item 14): identical to the base column. */}
                  {staticTh("quoteQualityScore", "Quote Qual")}
                  {staticTh("bidSize", "Bid Sz")}
                  {staticTh("askSize", "Ask Sz")}
                  {staticTh("sizingMaxInitialQty", "Sizing Max")}
                  <th className={thClass} style={thStyle}>
                    Insight
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sortedPurchases.map((p, i) => {
              const contractKey = `${String(p.conid)}-${p.expiry}`;
              const ticketState = ticketStateByConid[String(p.conid)];
              const actionBusy = stagingAll || stagingConid === String(p.conid);
              const isExpanded = showAnalyticsColumns && expandedContractKey === contractKey;
              const entryDrivers = p.entryScoreDrivers || null;
              const entryContribs = entryDrivers?.contributions || null;
              const entryRaw = entryDrivers?.raw || null;
              const entryWeights = entryDrivers?.weights || null;
              const surfaceDrivers = p.surfaceScoreDrivers || null;
              const surfaceScores = surfaceDrivers?.scores || null;
              const surfaceContext = surfaceDrivers?.context || null;
              const selected = isSelectedCandidate(p);
              const ranked = isRankedCandidate(p);
              const decisionReason = ranked
                ? p.whyIncluded || p.decisionReason || p.skipReasonDetail || "Ranked market screen; no sizing/allocation applied."
                : selected
                ? p.selectionReason || "Selected allocation"
                : p.skipReasonDetail || titleizeSkipReason(p.skipReason);
              // 18 base columns + the selection checkbox + analytics when shown.
              // Drives the expanded drawer row's colSpan and MUST equal the
              // number of <th>/<td> actually rendered, or the drawer misaligns.
              // 17 base / 13 analytics since Screener Phase 2 (2026-10-28) -- see the constants' note.
              // History: 19 + 12 -> 17 + 11 when Screen/Regime/Right were cut,
              // then 17 -> 19 when Spread % / Vega/$1k were added (2026-08-11).
              // CORRECTED 2026-09-10: the analytics count was left at 11 when
              // Spread % / Quote Qual / Bid Sz / Ask Sz were added, so the
              // expanded row's colSpan was four short. Then OI moved base-ward,
              // taking base 19 -> 20 and analytics 15 -> 14. Recount both arrays
              // if either list changes; the harness cannot catch a stale colSpan.
              const columnCount = SCREENER_BASE_COLUMNS + (showAnalyticsColumns ? SCREENER_ANALYTICS_COLUMNS : 0);
              const rowKey = String(p.conid);
              // Any contract the CIO can still act on is tickable. The
              // allocator's opinion decides who supplies the SIZE, not
              // whether the contract may be bought.
              const tickable = !ticketState;
              const needsCioQty = !selected;
              const rowBlocker = queueBlocker(p);

              return (
              <React.Fragment key={contractKey}>
              <RankedContractRow
                actionBusy={actionBusy}
                cioQtyByConid={cioQtyByConid}
                contractKey={contractKey}
                data={data}
                decisionReason={decisionReason}
                i={i}
                isExpanded={isExpanded}
                lotExceptionByConid={lotExceptionByConid}
                lotPreviewFor={lotPreviewFor}
                lotRule={lotRule}
                marketRankOnly={marketRankOnly}
                needsCioQty={needsCioQty}
                p={p}
                resolveTicketSizing={resolveTicketSizing}
                rowBlocker={rowBlocker}
                rowKey={rowKey}
                rungByExpiry={rungByExpiry}
                selected={selected}
                selectedForQueue={selectedForQueue}
                setCioQtyByConid={setCioQtyByConid}
                setExpandedContractKey={setExpandedContractKey}
                setLotExceptionByConid={setLotExceptionByConid}
                setSelectedForQueue={setSelectedForQueue}
                showAnalyticsColumns={showAnalyticsColumns}
                stageSinglePurchase={stageSinglePurchase}
                stagingAll={stagingAll}
                tickable={tickable}
                ticketState={ticketState}
                trustHardBlock={trustHardBlock}
              />
              {isExpanded ? (
                <RankedContractDetail
                  columnCount={columnCount}
                  entryContribs={entryContribs}
                  entryRaw={entryRaw}
                  entryWeights={entryWeights}
                  p={p}
                  surfaceContext={surfaceContext}
                  surfaceDrivers={surfaceDrivers}
                  surfaceScores={surfaceScores}
                />
              ) : null}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </CardContent>
    </Card>
    </section>
  );
}
